import {
  Alert,
  Badge,
  Card,
  Grid,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usePlaeneList } from '@/api/usePlaene';
import { useSnapshot } from '@/api/useSnapshot';
import { useAppStore } from '@/store/appStore';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import type { PlanStatus } from '@/api/types';

function ampelColor(status: PlanStatus): string {
  switch (status) {
    case 'final':
      return 'green';
    case 'review':
      return 'yellow';
    case 'archived':
      return 'gray';
    case 'draft':
    default:
      return 'blue';
  }
}

function formatEuro(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
}

export function CockpitPage() {
  const { t, i18n } = useTranslation(['cockpit', 'common']);
  const einrichtungId = useAppStore((s) => s.activeEinrichtungId);
  const jahr = useAppStore((s) => s.activeFoerderjahr);

  const filters = useMemo(
    () =>
      einrichtungId ? { einrichtung_id: einrichtungId, foerderjahr: jahr } : { foerderjahr: jahr },
    [einrichtungId, jahr],
  );
  const plaeneQuery = usePlaeneList(filters);
  const firstPlanId = plaeneQuery.data?.[0]?.id ?? null;
  const snapshotQuery = useSnapshot(firstPlanId);

  if (!einrichtungId) {
    return (
      <Stack>
        <Title order={2}>{t('cockpit:title')}</Title>
        <EmptyState
          title={t('cockpit:noPlanSelected')}
          description={t('cockpit:noEinrichtung')}
        />
      </Stack>
    );
  }

  if (plaeneQuery.isLoading) return <LoadingState />;
  if (plaeneQuery.isError) {
    return <ErrorState error={plaeneQuery.error} onRetry={() => void plaeneQuery.refetch()} />;
  }

  const plaene = plaeneQuery.data ?? [];
  const counts = {
    aktiv: plaene.filter((p) => p.status === 'draft' || p.status === 'review').length,
    draft: plaene.filter((p) => p.status === 'draft').length,
    final: plaene.filter((p) => p.status === 'final').length,
  };
  const snap = snapshotQuery.data;

  const chartData = (snap?.monatswerte ?? []).map((m) => ({
    monat: t(`common:month.short.${m.monat}`),
    [t('cockpit:chart.legend.einnahmen')]: m.einnahmen,
    [t('cockpit:chart.legend.ausgaben')]: m.ausgaben,
    [t('cockpit:chart.legend.saldo')]: m.saldo,
  }));

  return (
    <Stack gap="md">
      <Title order={2}>{t('cockpit:title')}</Title>
      <Text c="dimmed">{t('cockpit:subtitle')}</Text>

      {snap?.warnings?.length ? (
        <Stack gap={4}>
          {snap.warnings.map((w) => (
            <Alert
              key={`${w.code}-${w.message}`}
              color={w.level === 'error' ? 'red' : w.level === 'warn' ? 'yellow' : 'blue'}
              icon={w.level === 'info' ? <IconInfoCircle size={16} /> : <IconAlertTriangle size={16} />}
              variant="light"
            >
              {w.message}
            </Alert>
          ))}
        </Stack>
      ) : null}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card withBorder padding="md">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('cockpit:tile.activePlaene')}
            </Text>
            <Title order={3}>{counts.aktiv}</Title>
          </Stack>
        </Card>
        <Card withBorder padding="md">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('cockpit:tile.draftPlaene')}
            </Text>
            <Title order={3}>{counts.draft}</Title>
          </Stack>
        </Card>
        <Card withBorder padding="md">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('cockpit:tile.finalPlaene')}
            </Text>
            <Title order={3}>{counts.final}</Title>
          </Stack>
        </Card>
        <Card withBorder padding="md">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('cockpit:tile.gesamtErgebnis')}
            </Text>
            <Title order={3}>
              {snap ? formatEuro(snap.kpis.saldo, i18n.language) : '—'}
            </Title>
          </Stack>
        </Card>
      </SimpleGrid>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Paper withBorder p="md">
            <Title order={4} mb="sm">
              {t('cockpit:chart.title')}
            </Title>
            {chartData.length === 0 ? (
              <EmptyState height={260} />
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monat" />
                    <YAxis />
                    <ReTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey={t('cockpit:chart.legend.einnahmen')}
                      stroke="#1971c2"
                    />
                    <Line
                      type="monotone"
                      dataKey={t('cockpit:chart.legend.ausgaben')}
                      stroke="#e03131"
                    />
                    <Line
                      type="monotone"
                      dataKey={t('cockpit:chart.legend.saldo')}
                      stroke="#2f9e44"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack>
            <Paper withBorder p="md">
              <Title order={5} mb="sm">
                {t('cockpit:section.hinweise')}
              </Title>
              <List spacing={4} size="sm">
                {plaene.map((p) => (
                  <List.Item key={p.id}>
                    <Group gap={6}>
                      <Badge color={ampelColor(p.status)} variant="light">
                        {t(`common:status.${p.status}`)}
                      </Badge>
                      <Text>{p.name}</Text>
                    </Group>
                  </List.Item>
                ))}
              </List>
            </Paper>
            <Paper withBorder p="md">
              <Title order={5} mb="sm">
                {t('cockpit:top3.einnahmen')}
              </Title>
              <Text c="dimmed" size="sm">
                {t('cockpit:top3.empty')}
              </Text>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
